import { Group, Button, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo } from "react";
import {
  CreateLocationMonthlyFeeInput,
  useCreateLocationMonthlyFeeMutation,
  useUpdateLocationMonthlyFeeMutation,
} from "src/generated/graphql";
import { object, string } from "yup";
import { closeAllModals } from "@mantine/modals";
import { IMonthlyFeeElement } from ".";
import NumberInput from "src/components/NumberInput/NumberInput";
import TextInput from "src/components/TextInput/TextInput";

type IFormProps = CreateLocationMonthlyFeeInput;

export const LocationMonthlyFeeFormSchema = object().shape({
  name: string()
    .required("Name is required")
    .max(30, "Name must not be greater than 30 letters"),
  amount: string().required("Monthlly Cost is required"),
});

interface IProps {
  formData?: IMonthlyFeeElement;
  locationRevenueId: string;
  locationId: string;
  refetch: () => void;
}

const MonthlyFeeDetail = ({
  formData,
  locationRevenueId,
  locationId,
  refetch,
}: IProps) => {
  const theme = useMantineTheme();
  const form = useForm<IFormProps>({
    initialValues: {
      name: formData?.name || "",
      amount: formData?.amount || 0,
      location: { id: locationId },
      locationRevenue: { id: locationRevenueId },
    },
    validate: yupResolver(LocationMonthlyFeeFormSchema),
  });

  const [createFee, { loading: createLoading }] =
    useCreateLocationMonthlyFeeMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const [updateFee, { loading: editLoading }] =
    useUpdateLocationMonthlyFeeMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const handleUpdate = async (values: IFormProps) => {
    formData?.id
      ? updateFee({
          variables: {
            data: values,
            id: formData.id,
          },
        })
      : createFee({
          variables: {
            data: values,
          },
        });
  };

  return (
    <form onSubmit={form.onSubmit((values) => handleUpdate(values))}>
      <TextInput withAsterisk label="Name" {...form.getInputProps("name")} />

      <NumberInput
        withAsterisk
        label="Monthly Cost"
        precision={2}
        {...form.getInputProps("amount")}
      />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={() => closeAllModals()}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={createLoading || editLoading}
        >
          {formData?.id ? "Save" : "Create"}
        </Button>
      </Group>
    </form>
  );
};

export default memo(MonthlyFeeDetail);
